/**
 * FacilitatorService
 * Service for managing course facilitators (keyed by userId)
 */
import { BaseService } from '../base/BaseService';
/**
 * Service for facilitator operations (enriched views, current-user lookup)
 */
export class FacilitatorService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? FacilitatorService.DEFAULT_SERVICE_PATH,
            basePath: FacilitatorService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * List facilitators, optionally filtered
     * @param statusId - Optional FacilitatorStatus ID filter
     * @param search - Optional free-text search
     * @returns Array of facilitator model views
     */
    async list(statusId, search) {
        const response = await this.listRaw(statusId, search);
        return response.data ?? [];
    }
    /**
     * List facilitators with full response
     * @param statusId - Optional FacilitatorStatus ID filter
     * @param search - Optional free-text search
     * @returns ResponseData with array of facilitator model views
     */
    listRaw(statusId, search, skip, take) {
        const url = this.buildUrlWithParams(undefined, {
            statusId,
            search,
            skip,
            take,
        });
        return this.http.get(url);
    }
    /**
     * Get the current authenticated user's facilitator profile
     * @returns Facilitator model view for the current user
     */
    async me() {
        const response = await this.meRaw();
        if (!response.data) {
            throw new Error('Facilitator profile for the current user not found');
        }
        return response.data;
    }
    /**
     * Get the current authenticated user's facilitator profile with full response
     * @returns ResponseData with facilitator model view
     */
    meRaw() {
        const url = this.buildUrl('me');
        return this.http.get(url);
    }
    /**
     * Get a facilitator by user ID
     * @param userId - The facilitator user ID
     * @returns Facilitator model view
     */
    async getByUserId(userId) {
        const response = await this.getByUserIdRaw(userId);
        if (!response.data) {
            throw new Error(`Facilitator with user ID ${userId} not found`);
        }
        return response.data;
    }
    /**
     * Get a facilitator by user ID with full response
     * @param userId - The facilitator user ID
     * @returns ResponseData with facilitator model view
     */
    getByUserIdRaw(userId) {
        const url = this.buildUrl(userId);
        return this.http.get(url);
    }
    /**
     * Create a facilitator
     * @param data - Create payload
     * @returns The created facilitator
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create facilitator');
        }
        return response.data;
    }
    /**
     * Create a facilitator with full response
     * @param data - Create payload
     * @returns ResponseData with the created facilitator
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    /**
     * Update a facilitator
     * @param userId - The facilitator user ID
     * @param data - Update payload
     * @returns The updated facilitator
     */
    async update(userId, data) {
        const response = await this.updateRaw(userId, data);
        if (!response.data) {
            throw new Error(`Failed to update facilitator with user ID ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a facilitator with full response
     * @param userId - The facilitator user ID
     * @param data - Update payload
     * @returns ResponseData with the updated facilitator
     */
    updateRaw(userId, data) {
        const url = this.buildUrl(userId);
        return this.http.put(url, data);
    }
    /**
     * Delete a facilitator
     * @param userId - The facilitator user ID
     * @returns True if deleted successfully
     */
    async delete(userId) {
        const response = await this.deleteRaw(userId);
        return response.success;
    }
    /**
     * Delete a facilitator with full response
     * @param userId - The facilitator user ID
     * @returns ResponseData (success/fail)
     */
    deleteRaw(userId) {
        const url = this.buildUrl(userId);
        return this.http.delete(url);
    }
    /**
     * Change only the facilitator's status.
     * PATCH api/facilitators/{userId}/change-status/{newStatus}
     */
    async changeStatus(userId, newStatus) {
        const response = await this.changeStatusRaw(userId, newStatus);
        if (!response.data)
            throw new Error(`Failed to change status for facilitator ${userId}`);
        return response.data;
    }
    changeStatusRaw(userId, newStatus) {
        return this.http.patch(this.buildUrl(`${userId}/change-status/${newStatus}`));
    }
    // ---------------------------------------------------------------------------
    // Registrations
    // ---------------------------------------------------------------------------
    /**
     * List the registrations a facilitator is assigned to
     * @param userId - The facilitator user ID
     * @returns Array of facilitator registration views
     */
    async getRegistrations(userId) {
        const response = await this.getRegistrationsRaw(userId);
        return response.data ?? [];
    }
    /**
     * List the registrations a facilitator is assigned to with full response
     * @param userId - The facilitator user ID
     * @returns ResponseData with array of facilitator registration views
     */
    getRegistrationsRaw(userId) {
        const url = this.buildUrl(`${userId}/registrations`);
        return this.http.get(url);
    }
    /**
     * Get per-facilitator load statistics for the admin overview
     * @returns Array of facilitator load rows, descending by activeCount
     */
    async loadStats() {
        const response = await this.loadStatsRaw();
        return response.data ?? [];
    }
    /**
     * Get per-facilitator load statistics with full response
     * @returns ResponseData with array of facilitator load rows
     */
    loadStatsRaw() {
        const url = this.buildUrl('stats/load');
        return this.http.get(url);
    }
    /**
     * Assign a facilitator to a registration
     * @param userId - The facilitator user ID
     * @param body - Registration request payload
     * @returns The created registration-facilitator link
     */
    async addRegistration(userId, body) {
        const response = await this.addRegistrationRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to add registration to facilitator ${userId}`);
        }
        return response.data;
    }
    /**
     * Assign a facilitator to a registration with full response
     * @param userId - The facilitator user ID
     * @param body - Registration request payload
     * @returns ResponseData with the created registration-facilitator link
     */
    addRegistrationRaw(userId, body) {
        const url = this.buildUrl(`${userId}/registrations`);
        return this.http.post(url, body);
    }
    /**
     * Update a facilitator's registration assignment
     * @param userId - The facilitator user ID
     * @param registrationId - The registration ID
     * @param body - Registration request payload
     * @returns The updated registration-facilitator link
     */
    async updateRegistration(userId, registrationId, body) {
        const response = await this.updateRegistrationRaw(userId, registrationId, body);
        if (!response.data) {
            throw new Error(`Failed to update registration ${registrationId} for facilitator ${userId}`);
        }
        return response.data;
    }
    /**
     * Update a facilitator's registration assignment with full response
     * @param userId - The facilitator user ID
     * @param registrationId - The registration ID
     * @param body - Registration request payload
     * @returns ResponseData with the updated registration-facilitator link
     */
    updateRegistrationRaw(userId, registrationId, body) {
        const url = this.buildUrl(`${userId}/registrations/${registrationId}`);
        return this.http.put(url, body);
    }
    /**
     * Remove a facilitator's registration assignment
     * @param userId - The facilitator user ID
     * @param registrationId - The registration ID
     * @returns True if removed successfully
     */
    async removeRegistration(userId, registrationId) {
        const response = await this.removeRegistrationRaw(userId, registrationId);
        return response.success;
    }
    /**
     * Remove a facilitator's registration assignment with full response
     * @param userId - The facilitator user ID
     * @param registrationId - The registration ID
     * @returns ResponseData (success/fail)
     */
    removeRegistrationRaw(userId, registrationId) {
        const url = this.buildUrl(`${userId}/registrations/${registrationId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Courses (FacilitatorCourse junction — courses the facilitator can run)
    // ---------------------------------------------------------------------------
    /** List the courses a facilitator is approved to run. */
    async getCourses(userId) {
        const response = await this.getCoursesRaw(userId);
        return response.data ?? [];
    }
    getCoursesRaw(userId) {
        const url = this.buildUrl(`${userId}/courses`);
        return this.http.get(url);
    }
    /** Add a course coverage row to a facilitator. */
    async addCourse(userId, body) {
        const response = await this.addCourseRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to add course to facilitator ${userId}`);
        }
        return response.data;
    }
    addCourseRaw(userId, body) {
        const url = this.buildUrl(`${userId}/courses`);
        return this.http.post(url, body);
    }
    /** Update a facilitator's course coverage row. */
    async updateCourse(userId, facilitatorCourseId, body) {
        const response = await this.updateCourseRaw(userId, facilitatorCourseId, body);
        if (!response.data) {
            throw new Error(`Failed to update course ${facilitatorCourseId} for facilitator ${userId}`);
        }
        return response.data;
    }
    updateCourseRaw(userId, facilitatorCourseId, body) {
        const url = this.buildUrl(`${userId}/courses/${facilitatorCourseId}`);
        return this.http.put(url, body);
    }
    /** Remove a facilitator's course coverage row. */
    async removeCourse(userId, facilitatorCourseId) {
        const response = await this.removeCourseRaw(userId, facilitatorCourseId);
        return response.success;
    }
    removeCourseRaw(userId, facilitatorCourseId) {
        const url = this.buildUrl(`${userId}/courses/${facilitatorCourseId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Locations (FacilitatorLocation junction — locations the facilitator serves)
    // ---------------------------------------------------------------------------
    /** List the locations a facilitator can serve. */
    async getLocations(userId) {
        const response = await this.getLocationsRaw(userId);
        return response.data ?? [];
    }
    getLocationsRaw(userId) {
        const url = this.buildUrl(`${userId}/locations`);
        return this.http.get(url);
    }
    /** Add a location service row to a facilitator. */
    async addLocation(userId, body) {
        const response = await this.addLocationRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to add location to facilitator ${userId}`);
        }
        return response.data;
    }
    addLocationRaw(userId, body) {
        const url = this.buildUrl(`${userId}/locations`);
        return this.http.post(url, body);
    }
    /** Update a facilitator's location service row. */
    async updateLocation(userId, facilitatorLocationId, body) {
        const response = await this.updateLocationRaw(userId, facilitatorLocationId, body);
        if (!response.data) {
            throw new Error(`Failed to update location ${facilitatorLocationId} for facilitator ${userId}`);
        }
        return response.data;
    }
    updateLocationRaw(userId, facilitatorLocationId, body) {
        const url = this.buildUrl(`${userId}/locations/${facilitatorLocationId}`);
        return this.http.put(url, body);
    }
    /** Remove a facilitator's location service row. */
    async removeLocation(userId, facilitatorLocationId) {
        const response = await this.removeLocationRaw(userId, facilitatorLocationId);
        return response.success;
    }
    removeLocationRaw(userId, facilitatorLocationId) {
        const url = this.buildUrl(`${userId}/locations/${facilitatorLocationId}`);
        return this.http.delete(url);
    }
    // ---------------------------------------------------------------------------
    // Documents (FacilitatorDocument — documents attached to the facilitator)
    // ---------------------------------------------------------------------------
    /** List a facilitator's documents. */
    async getDocuments(userId) {
        const response = await this.getDocumentsRaw(userId);
        return response.data ?? [];
    }
    getDocumentsRaw(userId) {
        const url = this.buildUrl(`${userId}/documents`);
        return this.http.get(url);
    }
    /** Add a document to a facilitator. */
    async addDocument(userId, body) {
        const response = await this.addDocumentRaw(userId, body);
        if (!response.data) {
            throw new Error(`Failed to attach document to facilitator ${userId}`);
        }
        return response.data;
    }
    addDocumentRaw(userId, body) {
        const url = this.buildUrl(`${userId}/documents`);
        return this.http.post(url, body);
    }
    /** Update a facilitator's document. */
    async updateDocument(userId, facilitatorDocumentId, body) {
        const response = await this.updateDocumentRaw(userId, facilitatorDocumentId, body);
        if (!response.data) {
            throw new Error(`Failed to update document ${facilitatorDocumentId} for facilitator ${userId}`);
        }
        return response.data;
    }
    updateDocumentRaw(userId, facilitatorDocumentId, body) {
        const url = this.buildUrl(`${userId}/documents/${facilitatorDocumentId}`);
        return this.http.put(url, body);
    }
    /** Remove a facilitator's document. */
    async removeDocument(userId, facilitatorDocumentId) {
        const response = await this.removeDocumentRaw(userId, facilitatorDocumentId);
        return response.success;
    }
    removeDocumentRaw(userId, facilitatorDocumentId) {
        const url = this.buildUrl(`${userId}/documents/${facilitatorDocumentId}`);
        return this.http.delete(url);
    }
}
FacilitatorService.DEFAULT_SERVICE_PATH = 'ac-courses';
FacilitatorService.BASE_PATH = 'api/facilitators';
//# sourceMappingURL=FacilitatorService.js.map